import { AloftPrice, PairedSalesAnalysisFeature, ToolkitProperty } from '@aloft/toolkit-shared';
import { Block, Flex, Icon, IconButton } from '@aloft/toolkit-sui';
import { Alert } from '@aloft/toolkit-sui/chakra';

import { useWatch } from 'react-hook-form';

import { CustomToolkitPropertyTable } from '../../../components/ToolkitPropertyTable/CustomToolkitPropertyTable.tsx';
import { useZodFormContext } from '../../../hooks/useZodForm.ts';
import { makeAdjustResult } from '../../studies/utils/makeAdjustResult.ts';
import { useUpdateReferenceBarPropertiesAndActiveID } from '../hooks/useUpdateReferenceBarPropertiesAndActiveID.ts';
import {
  SHARED_RECONCILIATION_COLUMNS,
  SHARED_RECONCILIATION_COLUMN_CONFIG,
  StudiesData,
} from '../reconciliationTableSharedColumns.tsx';
import { schPair } from '../schemas.ts';
import { PairedSalesFormSchema, ReconciliationTableResults } from '../types.ts';
import { ReconciliationStatistics } from './ReconciliationStatistics.tsx';

const initialResults: ReconciliationTableResults = [];

export const ReconciliationTable = ({
  feature,
  activePairIndex,
  onRowSelect,
  onDeletePair,
}: {
  feature: PairedSalesAnalysisFeature;
  activePairIndex: number;
  onRowSelect: (index: number) => void;
  onDeletePair: (p1: ToolkitProperty, p2: ToolkitProperty) => void;
}) => {
  const { control } = useZodFormContext<PairedSalesFormSchema>();

  const selectedPairs = useWatch({ name: 'selectedPairs', control });
  let results: ReconciliationTableResults = initialResults;
  let resultsParsedOK = true;

  try {
    results = selectedPairs.map((p) => {
      const pair = schPair.parse(p);

      return {
        ...pair,
        result: makeAdjustResult(pair, AloftPrice.zero, feature),
        feature,
      };
    });
    resultsParsedOK = true;
  } catch (e) {
    resultsParsedOK = false;
  }

  useUpdateReferenceBarPropertiesAndActiveID(results, activePairIndex);

  return resultsParsedOK ? (
    <Flex flexDirection="column" gap="4px">
      <Block borderColor="strokeDefault" borderWidth="1px" borderStyle="solid">
        <CustomToolkitPropertyTable
          columns={[
            ...SHARED_RECONCILIATION_COLUMNS,
            {
              id: 'custom$delete',
              title: '',
              selector: (x: StudiesData) => x,
              toCell: (x: StudiesData) => {
                // don't show the trash can if there's only one pair left.
                if (results.length <= 1) return null;

                return (
                  <IconButton
                    icon="delete-bin"
                    onClick={() => {
                      const { p1, p2 } = x;
                      onDeletePair(p1, p2);
                    }}
                    ariaLabel="Delete Pair"
                    variant="tertiary"
                  />
                );
              },
            },
          ]}
          data={results}
          tpColumnConfig={SHARED_RECONCILIATION_COLUMN_CONFIG}
          selectedRowIndices={[activePairIndex]}
          activeRowIndex={activePairIndex}
          emptyTableMessage="No pairs selected"
          onRowSelect={({ p1: d1, p2: d2 }) => {
            const pairIndex = selectedPairs.findIndex(({ p1, p2 }) =>
              [p1, p2].every((p) =>
                [d1.toolkitRecordID, d2.toolkitRecordID].includes(p.toolkitRecordID),
              ),
            );

            if (pairIndex > -1) {
              onRowSelect(pairIndex);
            } else {
              console.error({ d1, d2 });
              throw new Error(
                'Somehow a pair was selected that does not exist in the list of pairs',
              );
            }
          }}
        />
      </Block>

      <Block paddingLeft="8px">
        <ReconciliationStatistics results={results.map((r) => r.result)} />
      </Block>
    </Flex>
  ) : (
    <Alert status="error" width="598px">
      <Flex marginRight=".5rem" alignItems="center">
        <Icon size="lg" icon="error-warning" />
      </Flex>
      You have one or more errors in your adjustment grid. Please resolve them to see reconciliation
      table
    </Alert>
  );
};
